import { HttpHeaders } from '@angular/common/http';
import type { AfterViewInit, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { FeatureFlag } from 'app/common/enums/feature-flag.enum';

import { FeeDisclaimerService } from 'app/shared/services/fee-disclaimer.service';
import moment from 'moment-timezone';
import { StringConstants } from '../../rcr-exchange/constants/string-constants';
import { FmLoadingPageService } from '../../shared/services/fm-loading-page.service';
import { SessionService } from '../../shared/services/session.service';
import { UserService } from '../../shared/services/user.service';
import { MegasService } from '../services/megas.service';
import { PassDataService } from '../services/pass-data.service';

@Component({
  standalone: false,
  selector: 'invd-bulk-upload-confirmation-page',
  templateUrl: './bulk-upload-confirmation-page.component.html',
  styleUrls: ['./bulk-upload-confirmation-page.component.scss'],
})
export class BulkUploadConfirmationPageComponent implements OnInit, AfterViewInit {
  public headerText: string;
  public headerSubText: string;
  public isAllowToNavigateAway = true;
  public multiMegaCommitResponse: any;
  public committedMegaList: any[];
  public megaPricingSummaryList: any[];
  public showEarlyCloseLegend = false;
  public showSubmitPoolInfo = true;
  public featuretoggle: string[] = [];
  public exportType = 'excel';
  public isMayPricingMonth = false;
  public showEarlyCloseLegendPricingSummary = false;
  public exportPDFButtonClicked = false;
  public exportExcelLinkClicked = true;
  public disclaimerMessage = '';
  public summaryDisclaimer = '';

  private router = inject(Router);
  private passDataService = inject(PassDataService);
  private fmLoadingService = inject(FmLoadingPageService);
  private userService = inject(UserService);
  private sessionService = inject(SessionService);
  private feeDisclaimerService = inject(FeeDisclaimerService);
  private megaService = inject(MegasService);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    this.fmLoadingService.stopLoading();
    this.featuretoggle = this.userService.toggleFeatures;

    this.exportExcelLinkClicked = true;
    this.headerText = StringConstants.MEGA_BULK_UPLOAD_ENTER_DETAILS_SCREEN_HEADER;
    this.headerSubText = StringConstants.MEGA_BULK_CONFIRMATION_SUB_HEADER;
    this.multiMegaCommitResponse = this.passDataService.getMultiMegaCommitResponse();
    const pricingMonthFromResponse =
      this.multiMegaCommitResponse && this.multiMegaCommitResponse.pricingMonth.split('-')[1];
    if (pricingMonthFromResponse === '05') {
      this.isMayPricingMonth = true;
    }
    this.committedMegaList = this.multiMegaCommitResponse.megaPricingInfo[0].newMegaList;
    this.megaPricingSummaryList =
      this.multiMegaCommitResponse &&
      this.multiMegaCommitResponse.megaPricingInfo[0].existingMegaList;
    this.showSubmitPoolInfo =
      this.userService.roles && this.userService.roles.includes('FJ5_PoolInfo_Provider')
        ? true
        : false;
    this.lookUpForEarlyCloseFlag();
  }

  ngAfterViewInit() {
    this.navigationController();
  }

  private navigationController() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (event.url.indexOf('review-and-commit') > 0) {
          this.isAllowToNavigateAway = false;
        } else {
          this.isAllowToNavigateAway = true;
        }
      }
      if (!(event instanceof NavigationEnd)) {
        return;
      }
    });
  }

  canDeactivate(): Promise<boolean> | boolean {
    return this.isAllowToNavigateAway;
  }

  navigatePageTo(pageURL: string) {
    this.router.navigate([pageURL]);
  }

  public printWindow() {
    window.print();
  }

  public exportMultiMegaPDFConfirmationDetails() {
    this.exportPDFButtonClicked = true;
    this.fmLoadingService.isLoaderRequesting.set(true);

    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/pdf',
        'Content-Type': 'application/json',
        'x-fnma-sessionid': this.sessionService.sessionId,
        'x-fnma-channel': 'web',
        'x-fnma-sub-channel': this.sessionService.subChannel,
      }),
    };

    const exportConfirmationPageReq = this.getPoolNumberList(this.multiMegaCommitResponse);
    this.megaService
      .exportMultiMegaPDFConfirmationDetails(exportConfirmationPageReq, httpOptions)
      .subscribe({
        next: (data) => this.handlePDFExportConfirmationPageServiceSuccess(data),
        error: (error) => this.handleAWSExportConfirmationPageServiceFail(error),
      });
  }

  public exportMultiMegaExcelConfirmationDetails() {
    this.fmLoadingService.isLoaderRequesting.set(true);

    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'Content-Type': 'application/json',
        'x-fnma-sessionid': this.sessionService.sessionId,
        'x-fnma-channel': 'web',
        'x-fnma-sub-channel': this.sessionService.subChannel,
      }),
    };

    const exportConfirmationPageReq = this.getPoolNumberList(this.multiMegaCommitResponse);
    if (this.exportExcelLinkClicked) {
      this.megaService
        .exportMultiMegaExcelConfirmationDetails(exportConfirmationPageReq, httpOptions)
        .subscribe({
          next: (data) => this.handleEXCELExportConfirmationPageServiceSuccess(data),
          error: (error) => this.handleAWSExportConfirmationPageServiceFail(error),
        });
    }

    this.exportExcelLinkClicked = false;
  }

  public getPoolNumberList(multiMegaresponse): any {
    const poolNumberList: any = [];
    multiMegaresponse.megaPricingInfo[0].newMegaList.forEach(function (item) {
      poolNumberList.push(item.poolNumber);
    }, this);

    return poolNumberList;
  }

  private handlePDFExportConfirmationPageServiceSuccess(res) {
    let fileName = '';
    this.fmLoadingService.isLoaderRequesting.set(false);
    const downloadedTime = moment(new Date());
    let data = res;
    fileName = 'Pool_Confirmation_' + downloadedTime.format('YYYYMMDDHHmmss') + '.pdf';
    data = data.replace(/^[^,]+,/, '');
    data = data.replace(/\s/g, '');
    const byteCharacters = window.atob(data);
    const byteNumbers = Array.from({ length: byteCharacters.length });
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers as any);
    const blob = new Blob([byteArray], { type: 'application/pdf' });

    if ((window.navigator as any).msSaveOrOpenBlob) {
      (window.navigator as any).msSaveOrOpenBlob(blob, fileName);
    } else {
      const objectUrl: string = URL.createObjectURL(blob);
      const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    }

    this.exportPDFButtonClicked = false;
    this.cdr.markForCheck();
  }

  private handleEXCELExportConfirmationPageServiceSuccess(res) {
    let fileName = '';
    this.fmLoadingService.isLoaderRequesting.set(false);
    const downloadedTime = moment(new Date());
    let data = res;
    fileName = 'Pool_Confirmation_' + downloadedTime.format('YYYYMMDDHHmmss') + '.xlsx';
    data = data.replace(/^[^,]+,/, '');
    data = data.replace(/\s/g, '');
    const byteCharacters = window.atob(data);
    const byteNumbers = Array.from({ length: byteCharacters.length });
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers as any);
    const blob = new Blob([byteArray], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    if ((window.navigator as any).msSaveOrOpenBlob) {
      (window.navigator as any).msSaveOrOpenBlob(blob, fileName);
    } else {
      const objectUrl: string = URL.createObjectURL(blob);
      const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    }

    this.exportExcelLinkClicked = true;
    this.cdr.markForCheck();
  }

  private handleAWSExportConfirmationPageServiceFail(_error: any): void {
    this.fmLoadingService.isLoaderRequesting.set(false);
    this.exportExcelLinkClicked = true;
    this.cdr.markForCheck();
  }

  public navigatePageToCommittedListing() {
    this.router.navigate(['/mega-trade/committed-mega']);
  }

  public lookUpForEarlyCloseFlag() {
    const isEarlyCloseDateNewMegas =
      this.committedMegaList && this.committedMegaList.some((val) => val.earlyCloseFlag);
    const isEarlyCloseDateExistingMegas =
      this.megaPricingSummaryList && this.megaPricingSummaryList.some((val) => val.earlyCloseFlag);
    if (isEarlyCloseDateNewMegas) {
      this.showEarlyCloseLegend = true;
    }
    if (isEarlyCloseDateExistingMegas) {
      this.showEarlyCloseLegendPricingSummary = true;
    }
  }

  public allowUpload(): void {
    let path = '';

    path =
      this.featuretoggle && this.featuretoggle.includes(FeatureFlag.COLAT_UPD_MENU)
        ? 'mega-upload/upload-pool-info'
        : 'mega-trade/committed-mega';
    this.router.navigate([path]);
  }

  public needsDisclaimer(prefixCode: string): boolean {
    if (this.feeDisclaimerService.needsDisclaimer(prefixCode)) {
      this.disclaimerMessage = StringConstants.SUPERS_PREFIX_FEE_DISCLAIMER;
      return true;
    }
    return false;
  }

  public summaryNeedsDisclaimer(prefixCode: string): boolean {
    if (this.feeDisclaimerService.needsDisclaimer(prefixCode)) {
      this.summaryDisclaimer = StringConstants.SUPERS_PREFIX_FEE_DISCLAIMER;
      return true;
    }
    return false;
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
